import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../../components/feature/Layout';
import { useSEO, generateWebPageSchema } from '../../../utils/seo';

const ScienceSummaryPage: React.FC = () => {
  // FAQ設定
  const faqs = [
    {
      question: '理科が苦手な子は、どこから始めればいいですか？',
      answer: 'まずは「暗記分野」（生物・地学の用語）から始めましょう。計算問題が苦手でも、用語を覚えることで点数が取れる分野があります。成功体験を積み重ねることで、理科への苦手意識が減ります。',
    },
    {
      question: '物理・化学の計算問題が苦手な子への声かけは？',
      answer: '「公式を覚える」のではなく、「何を求めたいか」を一緒に考えることが大切です。「この問題、何が分かれば答えが出る？」と問いかけることで、子ども自身が考える力が育ちます。',
    },
    {
      question: '理科の実験・観察は、どう学習すればいいですか？',
      answer: '実験の「目的」と「結果」を整理することが大切です。「何を確かめるための実験か」「結果から何が分かるか」を意識すると、実験の意味が理解しやすくなります。',
    },
    {
      question: '中学理科の無料教材サイトは、どれを選べばいいですか？',
      answer: '基礎固めなら「ちびむすドリル」、動画で学びたいなら「平均ドリル」がおすすめです。高校受験対策には「無料で使える中学学習プリント」が便利です。',
    },
    {
      question: '居場所や家庭で理科を教える際の注意点は？',
      answer: '「暗記」と「理解」を分けて教えることが大切です。用語は暗記が必要ですが、現象の仕組みは「なぜそうなるか」を一緒に考えることで理解が深まります。',
    },
  ];

  // SEO設定
  useSEO({
    title: '中学理科まとめ｜物理・化学・生物・地学の学習ガイド｜キョウコレ',
    description: '中学理科の学習内容を学年別・単元別に整理。物理・化学・生物・地学の分野別整理と、おすすめの無料教材サイトをご紹介します。',
    keywords: '中学理科,理科まとめ,物理,化学,生物,地学,学習ガイド,無料教材',
    canonical: (import.meta.env.VITE_SITE_URL || 'https://example.com') + '/features/science-summary',
    jsonLd: generateWebPageSchema(
      '中学理科まとめ｜物理・化学・生物・地学の学習ガイド｜キョウコレ',
      '中学理科の学習内容を学年別・単元別に整理。物理・化学・生物・地学の分野別整理と、おすすめの無料教材サイトをご紹介します。',
      '/features/science-summary'
    ),
  });

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 py-12">
        <div className="max-w-5xl mx-auto px-4">
          {/* ヘッダー */}
          <div className="text-center mb-12">
            <div className="inline-block w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center text-4xl mb-4">
              🔬
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              中学理科の無料教材まとめ
            </h1>
            <p className="text-lg text-gray-600">
              学年別・分野別に整理した学習ガイドと、おすすめの無料教材サイトをご紹介
            </p>
          </div>

          {/* このページでわかること */}
          <div className="bg-white rounded-xl shadow-sm p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-4 border-purple-500 pb-2">
              このページでわかること
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-purple-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>中学理科を「物理」「化学」「生物」「地学」の4分野に分けて整理する方法</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-purple-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>学年別・分野別のつまずきポイントと攻略法</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-purple-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>無料で使える理科教材サイトの特徴と使い分け</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-purple-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>居場所や家庭で理科を教える際のコツ</span>
              </li>
            </ul>
          </div>

          {/* 理科はこう整理すると進めやすい */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-purple-500 pb-2">
              理科はこう整理すると進めやすい
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                中学理科は「物理」「化学」「生物」「地学」の4つの分野で構成されています。この4つは独立しているようで、実は深くつながっています。例えば、化学反応を理解するには物理の知識が必要ですし、生物の仕組みを理解するには化学の知識が役立ちます。
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                <strong>分野別の整理と暗記・計算の切り分け：</strong>理科が苦手な子の多くは、「暗記」と「計算」を混同しています。生物・地学は主に暗記分野、物理・化学は計算分野ですが、それぞれに暗記と計算の両方が含まれます。まずは「何を暗記すべきか」「何を計算で解くか」を整理することが、理科全体の理解につながります。
              </p>
              <div className="bg-purple-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">学習の進め方（推奨順序）</h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">1</span>
                    <div>
                      <strong>暗記分野の基礎固め</strong>：生物・地学の用語を覚える
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">2</span>
                    <div>
                      <strong>計算分野の理解</strong>：物理・化学の公式と計算方法を学ぶ
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">3</span>
                    <div>
                      <strong>実験・観察の整理</strong>：実験の目的と結果を理解する
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">4</span>
                    <div>
                      <strong>応用問題</strong>：複数の分野を組み合わせた問題に挑戦
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </section>

          {/* 学年別・分野別の整理 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-purple-500 pb-2">
              学年別・分野別の整理
            </h2>

            {/* 中1理科 */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-purple-500 text-white rounded-full flex items-center justify-center mr-3">1</span>
                中1理科のポイント
              </h3>
              <p className="text-gray-700 mb-4">
                中1では「植物」「物質」「光・音・力」「大地の変化」を学びます。理科の基礎となる観察・実験の方法も習得します。
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">植物の世界（生物分野）</h4>
                  <p className="text-sm text-gray-600">
                    植物の分類、光合成、呼吸などを学ぶ。<Link to="/materials?subject=理科" className="text-purple-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">身の回りの物質（化学分野）</h4>
                  <p className="text-sm text-gray-600">
                    物質の性質、状態変化、気体の性質を理解する。<Link to="/materials?subject=理科" className="text-purple-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">光・音・力（物理分野）</h4>
                  <p className="text-sm text-gray-600">
                    光の反射・屈折、音の性質、力のつり合いを学ぶ。<Link to="/materials?subject=理科" className="text-purple-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">大地の変化（地学分野）</h4>
                  <p className="text-sm text-gray-600">
                    火山、地震、地層などを理解する。<Link to="/materials?subject=理科" className="text-purple-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
              </div>
            </div>

            {/* 中2理科 */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-purple-500 text-white rounded-full flex items-center justify-center mr-3">2</span>
                中2理科のポイント
              </h3>
              <p className="text-gray-700 mb-4">
                中2では「動物」「化学変化」「電流」「気象」を学びます。中1の基礎がしっかりしていれば、スムーズに進められます。
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">動物の世界（生物分野）</h4>
                  <p className="text-sm text-gray-600">
                    動物の分類、消化・呼吸・循環などを学ぶ。<Link to="/materials?subject=理科" className="text-purple-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">化学変化と原子・分子（化学分野）</h4>
                  <p className="text-sm text-gray-600">
                    化学反応式、酸化・還元、化学変化と質量を理解する。<Link to="/materials?subject=理科" className="text-purple-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">電流とその利用（物理分野）</h4>
                  <p className="text-sm text-gray-600">
                    電流・電圧・抵抗、オームの法則、電力を学ぶ。<Link to="/materials?subject=理科" className="text-purple-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">気象とその変化（地学分野）</h4>
                  <p className="text-sm text-gray-600">
                    天気の変化、気圧、前線などを理解する。<Link to="/materials?subject=理科" className="text-purple-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
              </div>
            </div>

            {/* 中3理科 */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-purple-500 text-white rounded-full flex items-center justify-center mr-3">3</span>
                中3理科のポイント
              </h3>
              <p className="text-gray-700 mb-4">
                中3では「生命の連続性」「化学変化とイオン」「運動とエネルギー」「地球と宇宙」を学びます。高校受験対策も本格化するため、計画的な学習が必要です。
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">生命の連続性（生物分野）</h4>
                  <p className="text-sm text-gray-600">
                    細胞分裂、遺伝、生物の進化などを学ぶ。<Link to="/materials?subject=理科" className="text-purple-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">化学変化とイオン（化学分野）</h4>
                  <p className="text-sm text-gray-600">
                    イオン、酸・アルカリ、中和、電池などを理解する。<Link to="/materials?subject=理科" className="text-purple-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">運動とエネルギー（物理分野）</h4>
                  <p className="text-sm text-gray-600">
                    物体の運動、仕事とエネルギー、力学的エネルギーを学ぶ。<Link to="/materials?subject=理科" className="text-purple-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">地球と宇宙（地学分野）</h4>
                  <p className="text-sm text-gray-600">
                    天体の動き、太陽系、宇宙の広がりを理解する。<Link to="/materials?subject=理科" className="text-purple-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* おすすめ無料教材サイト */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-purple-500 pb-2">
              おすすめ無料教材サイト一覧
            </h2>
            <p className="text-gray-700 mb-6">
              中学理科の学習に役立つ無料教材サイトを厳選しました。それぞれの特徴を理解して、目的に合わせて使い分けましょう。
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://chugaku.manabihiroba.net/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-700 hover:underline cursor-pointer"
                  >
                    無料で使える中学学習プリント
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>物理・化学・生物・地学を体系的に学習できる
                </p>
                <p className="text-sm text-gray-700">
                  中1から中3まで、すべての分野のプリントが揃っています。基本問題から応用問題まで段階的に用意されています。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://happylilac.net/jhs-science1.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-700 hover:underline cursor-pointer"
                  >
                    ちびむすドリル 中学理科
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>要点まとめと練習問題で効率的に復習できる。印刷向け
                </p>
                <p className="text-sm text-gray-700">
                  学年別・単元別に整理されたプリントが豊富です。基礎固めに特化しており、理科が苦手な子でも取り組みやすい内容です。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://heikin.space/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-700 hover:underline cursor-pointer"
                  >
                    平均ドリル
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>無料授業動画とプリントで理解を深める。自習向け
                </p>
                <p className="text-sm text-gray-700">
                  授業動画を見てから問題を解く形式で、自宅学習に最適です。実験の様子も動画で確認できます。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://potablog.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-700 hover:underline cursor-pointer"
                  >
                    ポテスクプリント
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>QRコードで答え合わせができる理科教材
                </p>
                <p className="text-sm text-gray-700">
                  QRコードを読み取ると解答が表示される便利な仕組みで、自習に最適です。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <Link
                    to="/sites"
                    className="text-purple-600 hover:text-purple-700 hover:underline cursor-pointer"
                  >
                    その他の理科教材サイトを見る
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </h3>
                <p className="text-sm text-gray-700">
                  キョウコレでは、さらに多くの無料教材サイトを紹介しています。教科や単元で絞り込んで検索できます。
                </p>
              </div>
            </div>
          </section>

          {/* 居場所・家庭での使い方 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-purple-500 pb-2">
              居場所・家庭での使い方
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                理科は「暗記」と「理解」を分けて教えることが大切です。居場所や家庭で理科を教える際のコツをまとめました。
              </p>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">1. 「暗記」と「理解」を分ける</h3>
                  <p className="text-gray-700 leading-relaxed">
                    用語は暗記が必要ですが、現象の仕組みは「なぜそうなるか」を一緒に考えることで理解が深まります。「この用語、覚えておこうね」「この現象、なぜこうなると思う？」と使い分けましょう。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">2. 「実験の目的と結果を整理する」</h3>
                  <p className="text-gray-700 leading-relaxed">
                    実験問題では、「何を確かめるための実験か」「結果から何が分かるか」を意識することが大切です。「この実験、何を確かめたかったんだっけ？」と問いかけると、子どもも考えやすくなります。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">3. 「図やグラフを一緒に描く」</h3>
                  <p className="text-gray-700 leading-relaxed">
                    物理・化学の問題は、図やグラフを描くことで理解しやすくなります。「この部分、図に描いてみようか」と声をかけて、一緒に視覚化しましょう。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">4. 活用レシピを参考にする</h3>
                  <p className="text-gray-700 leading-relaxed">
                    キョウコレの<Link to="/recipes" className="text-purple-600 hover:underline">活用レシピ</Link>では、他の居場所や家庭での実践例が共有されています。「こんな声かけが効果的だった」「この教材の使い方が良かった」といったアイデアを参考にしてみましょう。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-green-500 pb-2">
              よくある質問
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-start">
                    <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      Q
                    </span>
                    {faq.question}
                  </h3>
                  <div className="ml-11">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ボラタス導線 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-green-500 pb-2">
              オンライン学習支援を探すなら（ボラタス）
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                教材だけでなく、オンラインで学習をサポートしてくれるボランティアを探すこともできます。居場所や家庭での学習支援に、ぜひご活用ください。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://boratasu.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 cursor-pointer whitespace-nowrap text-center"
                >
                  ボラタスでボランティアを探す
                  <i className="ri-external-link-line ml-2"></i>
                </a>
                <a
                  href="https://zion-craft.work/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 bg-slate-600 text-white font-medium rounded-lg hover:bg-slate-700 transition-colors duration-200 cursor-pointer whitespace-nowrap text-center"
                >
                  ボラタスについて詳しく知る
                  <i className="ri-external-link-line ml-2"></i>
                </a>
              </div>
            </div>
          </section>

          {/* まとめ */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl shadow-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">
              中学理科は、分野別に整理すれば理解しやすくなります
            </h2>
            <p className="text-lg leading-relaxed mb-6">
              このページで紹介した無料教材サイトや学習のコツを活用して、<br />
              子どもたちの「分かった！」を増やしていきましょう。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/materials?subject=理科"
                className="inline-block px-6 py-3 bg-white text-green-600 font-medium rounded-lg hover:bg-green-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                理科の単元ガイド一覧を見る
              </Link>
              <Link
                to="/sites"
                className="inline-block px-6 py-3 bg-white text-green-600 font-medium rounded-lg hover:bg-green-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                教材サイト一覧を見る
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ScienceSummaryPage;
